import { Field, Form } from "react-final-form";
import {
  SubscriptionPlanPaymentData,
  SubscriptionPlan,
  useGetAdminPanelSubscriptionsPaymentTypeQuery,
  useGetSchoolPayersQuery,
} from "../../../../generated/graphql";
import {
  Grid,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import { ButtonCustom, ModalInput } from "../../../shared/Style/Style";
import { validateForm, SubscriptionFormValues } from "./utils";
import { commonPaymentTypeMap, subscriptionPlansMonths } from "../maps";
import { costText } from "../../constants";

type CreatePaymentData = {
  data: Partial<SubscriptionPlanPaymentData>;
};

interface SubscriptionFormProps {
  subscriptionPlans: SubscriptionPlan[];
  disableSubmit: boolean;
  createPayment(variables: CreatePaymentData): void;
}

const formInitialValues: SubscriptionFormValues = {
  paymentDate: "",
  validity: "",
  paymentType: "",
  planId: "",
  pupilsCount: "",
  teachersCount: "",
  total: "",
  boardsCount: "",
};

export const SubscriptionFormSchools = ({
  subscriptionPlans = [],
  disableSubmit,
  createPayment,
}: SubscriptionFormProps) => {
  const { data: paymentTypeData } =
    useGetAdminPanelSubscriptionsPaymentTypeQuery();
  const { data: schoolPayerData } = useGetSchoolPayersQuery({
    fetchPolicy: "cache-and-network",
  });

  const paymentTypes =
    paymentTypeData?.getAdminPanelSubscriptionsPaymentType ?? [];

  const schoolPayer = schoolPayerData?.getSchoolPayers ?? [];

  const onFormValidation = (values: SubscriptionFormValues) =>
    validateForm(values, false);

  const onFormSubmit = (values: SubscriptionFormValues) => {
    const {
      paymentDate,
      validity,
      paymentType,
      planId,
      pupilsCount,
      teachersCount,
      total,
      payerId,
      boardsCount,
    } = values;

    if (!validity || isNaN(Number(validity))) return;

    createPayment({
      data: {
        paymentDate,
        validity: +validity,
        paymentTypeId: paymentType,
        planId,
        pupilsCount: Number(pupilsCount) || 0,
        teachersCount: Number(teachersCount) || 0,
        payerId,
        total: Number(total) || 0,
        boardsCount: Number(boardsCount) || undefined,
      },
    });
  };

  return (
    <Form
      initialValues={formInitialValues}
      validate={onFormValidation}
      onSubmit={onFormSubmit}
    >
      {({ handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <Grid container justifyContent="flex-start" spacing={2}>
            <Grid xs={6} item>
              <Field name="paymentDate">
                {({ input, meta }) => (
                  <DatePicker
                    error={meta.error && meta.touched}
                    helperText={meta.touched && meta.error}
                    fullWidth
                    variant="inline"
                    format="dd.MM.yyyy"
                    label="Дата платежа"
                    inputVariant="filled"
                    value={input.value || null}
                    onChange={input.onChange}
                  />
                )}
              </Field>
            </Grid>

            <Grid xs={6} item>
              <Field name="validity">
                {({ input, meta }) => (
                  <ModalInput
                    label="Кол-во месяцев"
                    error={meta.error && meta.touched}
                    helperText={meta.touched && meta.error}
                    variant="filled"
                    type="text"
                    {...input}
                  />
                )}
              </Field>
            </Grid>

            <Grid xs={6} item>
              <Field name="planId">
                {({ input, meta }) => (
                  <FormControl
                    variant="filled"
                    error={meta.error && meta.touched}
                    fullWidth
                  >
                    <InputLabel>Тариф</InputLabel>
                    <Select
                      fullWidth
                      value={input.value.id}
                      onChange={(e) => {
                        input.onChange(e.target.value);
                      }}
                    >
                      {subscriptionPlans.map(({ id, name, description }) => (
                        <MenuItem key={id} value={id}>
                          {`${description ?? name} ${
                            subscriptionPlansMonths[name]?.label || ""
                          }`}
                        </MenuItem>
                      ))}
                    </Select>
                    {meta.error && meta.touched ? (
                      <FormHelperText>{meta.error}</FormHelperText>
                    ) : null}
                  </FormControl>
                )}
              </Field>
            </Grid>

            <Grid xs={6} item>
              <Field name="paymentType">
                {({ input, meta }) => (
                  <FormControl
                    variant="filled"
                    error={meta.error && meta.touched}
                    fullWidth
                  >
                    <InputLabel>Тип платежа</InputLabel>
                    <Select
                      fullWidth
                      value={input.value.id}
                      onChange={(e) => {
                        input.onChange(e.target.value);
                      }}
                    >
                      {paymentTypes.map(({ id, name }) => (
                        <MenuItem key={id} value={id}>
                          {commonPaymentTypeMap[name]}
                        </MenuItem>
                      ))}
                    </Select>
                    {meta.error && meta.touched ? (
                      <FormHelperText>{meta.error}</FormHelperText>
                    ) : null}
                  </FormControl>
                )}
              </Field>
            </Grid>

            <Grid xs={6} item>
              <Field name="pupilsCount">
                {({ input, meta }) => (
                  <ModalInput
                    label="Количество учеников"
                    error={meta.error && meta.touched}
                    helperText={meta.touched && meta.error}
                    variant="filled"
                    type="text"
                    {...input}
                  />
                )}
              </Field>
            </Grid>

            <Grid xs={6} item>
              <Field name="teachersCount">
                {({ input, meta }) => (
                  <ModalInput
                    label="Количество учителей"
                    error={meta.error && meta.touched}
                    helperText={meta.touched && meta.error}
                    variant="filled"
                    type="text"
                    {...input}
                  />
                )}
              </Field>
            </Grid>

            <Grid xs={6} item>
              <Field name="total">
                {({ input, meta }) => (
                  <ModalInput
                    label={costText}
                    helperText={meta.touched && meta.error}
                    variant="filled"
                    type="text"
                    {...input}
                  />
                )}
              </Field>
            </Grid>
            <Grid xs={6} item>
              <Field name="payerId">
                {({ input, meta }) => (
                  <FormControl
                    variant="filled"
                    error={meta.error && meta.touched}
                    fullWidth
                  >
                    <InputLabel>Плательщик</InputLabel>
                    <Select
                      fullWidth
                      value={input.value.id}
                      onChange={(e) => {
                        input.onChange(e.target.value);
                      }}
                    >
                      {schoolPayer.map(({ id, name }) => (
                        <MenuItem key={id} value={id}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                    {meta.error && meta.touched ? (
                      <FormHelperText>{meta.error}</FormHelperText>
                    ) : null}
                  </FormControl>
                )}
              </Field>
            </Grid>
            <Grid xs={6} item>
              <Field name="boardsCount">
                {({ input, meta }) => (
                  <ModalInput
                    label="Кількість дошок"
                    error={meta.touched && meta.error}
                    helperText={meta.touched && meta.error}
                    variant="filled"
                    type="text"
                    {...input}
                  />
                )}
              </Field>
            </Grid>

            <Grid xs={12} item>
              <ButtonCustom type="submit" disabled={disableSubmit} width="200">
                Оформити підписку
              </ButtonCustom>
            </Grid>
          </Grid>
        </form>
      )}
    </Form>
  );
};
